import { ref } from 'vue'
import apiInstance from '@/utils/api'

export type LocalizedName = Record<string, string>

export interface TimeSlot {
  startTime: string
  endTime: string
}

// Default redirect timings used when none are provided by the API.
const defaultRedirectTimings = (): Record<string, TimeSlot[]> => ({
  Monday: [{ startTime: '09:00', endTime: '22:00' }],
  Tuesday: [{ startTime: '09:00', endTime: '22:00' }],
  Wednesday: [{ startTime: '09:00', endTime: '22:00' }],
  Thursday: [{ startTime: '09:00', endTime: '22:00' }],
  Friday: [{ startTime: '09:00', endTime: '23:00' }],
  Saturday: [{ startTime: '10:00', endTime: '23:00' }],
  Sunday: [{ startTime: '10:00', endTime: '22:00' }],
})

export interface RestaurantSettingsData {
  pin: string
  initialOrderNumber: number
  orderButtonText: LocalizedName
  mainColor: string
  darkMode: boolean
  flag1: string
  flag2: string
  googleAnalyticsCode: string
  googleMapsLink: string
  externalImprintUrl: string
  hideCurrency: boolean
  // Redirect / return-url settings (moved here from Basic Information).
  // Optional: admin-only — non-admin callers omit them so they never hit the API.
  returnUrl?: string
  redirectToOutlet?: boolean
  redirectUrl?: string
  customizedTiming?: boolean
  redirectTimings?: Record<string, TimeSlot[]>
  matrixInterfaceActive?: boolean
  cloudPrinterActive?: boolean
  kioskActive?: boolean
}

export interface SettingsDraft extends RestaurantSettingsData {
  validationError?: string
}

const createEmptySettings = (): RestaurantSettingsData => ({
  pin: '',
  initialOrderNumber: 1000,
  orderButtonText: {
    en: 'Order now',
    de: 'Bestellen',
  },
  mainColor: '#3B82F6',
  darkMode: false,
  flag1: '',
  flag2: '',
  googleAnalyticsCode: '',
  googleMapsLink: '',
  externalImprintUrl: '',
  hideCurrency: false,
  returnUrl: '',
  redirectToOutlet: false,
  redirectUrl: '',
  customizedTiming: false,
  redirectTimings: defaultRedirectTimings(),
  matrixInterfaceActive: false,
  cloudPrinterActive: false,
  kioskActive: true,
})

// Parse the API's outlet_timings (array or object form) into form-friendly
// { startTime, endTime } slots keyed by weekday.
const parseOutletTimings = (outletTimings: any): Record<string, TimeSlot[]> => {
  const redirectTimings = defaultRedirectTimings()
  if (!outletTimings) return redirectTimings

  if (Array.isArray(outletTimings)) {
    // [{ week_day: "Monday", available_timings: ["09:00-22:00"] }, ...]
    outletTimings.forEach((timing: any) => {
      const day = timing.week_day
      if (day && redirectTimings.hasOwnProperty(day)) {
        if (Array.isArray(timing.available_timings) && timing.available_timings.length > 0) {
          redirectTimings[day] = timing.available_timings.map((slot: any) => {
            if (typeof slot === 'string') {
              const [startTime, endTime] = slot.split('-')
              return { startTime, endTime }
            } else if (typeof slot === 'object' && (slot.from || slot.startTime)) {
              return { startTime: slot.from || slot.startTime, endTime: slot.to || slot.endTime }
            }
            return { startTime: '09:00', endTime: '17:00' }
          })
        } else {
          redirectTimings[day] = []
        }
      }
    })
  } else if (typeof outletTimings === 'object') {
    // { "Monday": [{from,to}], "Tuesday": [], ... }
    Object.keys(outletTimings).forEach((day: string) => {
      if (redirectTimings.hasOwnProperty(day)) {
        const dayTimings = outletTimings[day]
        if (Array.isArray(dayTimings) && dayTimings.length > 0) {
          redirectTimings[day] = dayTimings.map((slot: any) => {
            if (slot.from && slot.to) return { startTime: slot.from, endTime: slot.to }
            if (slot.startTime && slot.endTime) return { startTime: slot.startTime, endTime: slot.endTime }
            if (typeof slot === 'string') {
              const [startTime, endTime] = slot.split('-')
              return { startTime, endTime }
            }
            return { startTime: '09:00', endTime: '17:00' }
          })
        } else {
          redirectTimings[day] = []
        }
      }
    })
  }

  return redirectTimings
}

// Map the redirect/return fields from an API payload into the store shape.
const parseRedirectSettings = (data: any) => ({
  returnUrl: data?.redirect_return_url || '',
  redirectToOutlet: data?.redirect_outlet_active === 1 || data?.redirect_outlet_active === true,
  redirectUrl: data?.outlet_url_link || '',
  customizedTiming: data?.outlet_customize_active === 1 || data?.outlet_customize_active === true,
  redirectTimings: parseOutletTimings(data?.outlet_timings),
})

// Build the redirect/return part of the settings save payload.
// Returns {} when the caller didn't provide redirect data (non-admin) so these
// params never reach the API and existing values are preserved server-side.
const buildRedirectPayload = (s: RestaurantSettingsData): Record<string, any> => {
  if (s.returnUrl === undefined && s.redirectToOutlet === undefined) {
    return {}
  }

  const payload: Record<string, any> = {
    redirect_return_url: s.returnUrl || '',
  }
  if (s.redirectToOutlet) {
    payload.redirect_outlet_active = 1
    payload.outlet_url_link = s.redirectUrl
    if (s.customizedTiming) {
      payload.outlet_customize_active = 1
      payload.outlet_timings = Object.entries(s.redirectTimings || {}).map(([week_day, slots]) => ({
        week_day,
        available_timings: Array.isArray(slots) && slots.length > 0
          ? slots.map((slot) => `${slot.startTime}-${slot.endTime}`)
          : [],
      }))
    } else {
      payload.outlet_customize_active = 0
      payload.outlet_timings = []
    }
  } else {
    payload.redirect_outlet_active = 0
    payload.outlet_customize_active = 0
    payload.outlet_timings = []
  }
  return payload
}

// Helper function to parse multi-language order button text from API response
const parseOrderButtonText = (data: any): LocalizedName => {
  let orderButtonTextLocalized: LocalizedName = { en: 'Order now', de: 'Bestellen' }

  if (data?.order_button_text) {
    if (typeof data.order_button_text === 'string') {
      // Legacy format: single string
      orderButtonTextLocalized = { en: data.order_button_text, de: data.order_button_text }
    } else if (typeof data.order_button_text === 'object') {
      // New format: { en: { name: "..." }, de: { name: "..." } }
      const normalized: LocalizedName = {}
      Object.entries(data.order_button_text).forEach(([lang, value]) => {
        if (typeof value === 'string') {
          // Flat string format
          normalized[lang] = value
        } else if (typeof value === 'object' && value !== null && 'name' in value) {
          // Nested format with name property
          normalized[lang] = (value as any).name || ''
        }
      })
      orderButtonTextLocalized = Object.keys(normalized).length > 0 ? normalized : { en: 'Order now', de: 'Bestellen' }
    }
  }

  return orderButtonTextLocalized
}

export const useSettings = (getRestaurantId: () => string) => {
  const settings = ref<RestaurantSettingsData>(createEmptySettings())
  const draft = ref<SettingsDraft>({
    ...createEmptySettings(),
    validationError: '',
  })

  const initialize = () => {
    settings.value = createEmptySettings()
    clearDraft()
  }

  const clearDraft = () => {
    draft.value = {
      ...settings.value,
      validationError: '',
    }
  }

  const initializeDraft = () => {
    draft.value = {
      ...settings.value,
      validationError: '',
    }
  }

  const updateDraft = (updates: Partial<SettingsDraft>) => {
    draft.value = {
      ...draft.value,
      ...updates,
    }
  }

  const getDraft = (): SettingsDraft => draft.value

  // Also set from the restaurant-info endpoint, so the tabs render without the full settings.
  const setModuleVisibility = (matrixInterfaceActive: boolean, cloudPrinterActive: boolean, kioskActive: boolean) => {
    settings.value = { ...settings.value, matrixInterfaceActive, cloudPrinterActive, kioskActive }
    draft.value = { ...draft.value, matrixInterfaceActive, cloudPrinterActive, kioskActive }
  }

  const loadSettings = async (): Promise<boolean> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.warn('⚠️ Restaurant ID is not available for loading settings')
      return false
    }

    try {

      const response = await apiInstance.get(`/restaurant/general-settings/${restaurantId}`)
      const data = response.data?.data

      if (data) {
        const orderButtonTextLocalized = parseOrderButtonText(data)

        settings.value = {
          pin: data.rest_pin_number || '',
          initialOrderNumber: parseInt(data.initial_order_no, 10) || 1000,
          orderButtonText: orderButtonTextLocalized,
          mainColor: data.app_main_color || '#3B82F6',
          darkMode: data.dark_mode_active === 1 || data.dark_mode_active === true,
          flag1: data.service_area_messages?.flag1 || '',
          flag2: data.service_area_messages?.flag2 || '',
          googleAnalyticsCode: data.google_analytic_code || '',
          googleMapsLink: data.google_map_link || '',
          externalImprintUrl: data.external_imprint_URL || '',
          hideCurrency: data.hide_currency === 1 || data.hide_currency === true,
          matrixInterfaceActive: data.show_matrix_interface === 1 || data.show_matrix_interface === true,
          cloudPrinterActive: data.show_cloud_printer === 1 || data.show_cloud_printer === true,
          kioskActive: data.show_kiosk === 1 || data.show_kiosk === true,
          ...parseRedirectSettings(data),
        }
        clearDraft()
        return true
      } else {
        console.warn('⚠️ Failed to load settings:', 'No data returned')
        return false
      }
    } catch (error: any) {
      console.error('🔴 Error loading settings:', error.message)
      console.error('🔴 Error response:', JSON.stringify(error.response?.data, null, 2))
      return false
    }
  }

  const saveSettings = async (settingsData: RestaurantSettingsData): Promise<{ success: boolean; data?: any; errors?: any }> => {
    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      console.error('🔴 Restaurant ID is not available for saving settings')
      return { success: false, errors: { general: ['Restaurant ID is not available'] } }
    }

    try {
      // Transform order button text to API format: { en: { name: "Order now", de: "Bestellen" } }
      const orderButtonTextPayload: Record<string, { name: string }> = {}
      if (settingsData.orderButtonText) {
        Object.entries(settingsData.orderButtonText).forEach(([lang, text]) => {
          orderButtonTextPayload[lang] = { name: text }
        })
      }

      // Map form data to API payload
      const payload: any = {
        rest_pin_number: settingsData.pin || null,
        initial_order_no: settingsData.initialOrderNumber.toString(),
        order_button_text: orderButtonTextPayload,
        app_main_color: settingsData.mainColor,
        dark_mode_active: settingsData.darkMode ? 1 : 0,
        external_imprint_URL: settingsData.externalImprintUrl || '',
        hide_currency: settingsData.hideCurrency ? 1 : 0,
        google_analytic_code: settingsData.googleAnalyticsCode || null,
        google_map_link: settingsData.googleMapsLink || null,
        service_area_messages: {
          flag1: settingsData.flag1,
          flag2: settingsData.flag2,
        },
        // Redirect / return-url settings (moved here from Basic Information)
        ...buildRedirectPayload(settingsData),
      }

      if (settingsData.matrixInterfaceActive !== undefined) {
        payload.show_matrix_interface = settingsData.matrixInterfaceActive ? 1 : 0
      }

      if (settingsData.cloudPrinterActive !== undefined) {
        payload.show_cloud_printer = settingsData.cloudPrinterActive ? 1 : 0
      }

      if (settingsData.kioskActive !== undefined) {
        payload.show_kiosk = settingsData.kioskActive ? 1 : 0
      }

      const response = await apiInstance.put(`/restaurant/general-settings/${restaurantId}`, payload)
      const responseData = response.data

      if (responseData.error === false || response.status === 200) {
        const data = responseData.data || {}
        const orderButtonTextLocalized = parseOrderButtonText(data)

        settings.value = {
          pin: data.rest_pin_number || '',
          initialOrderNumber: parseInt(data.initial_order_no, 10) || 1000,
          orderButtonText: orderButtonTextLocalized,
          mainColor: data.app_main_color || '#3B82F6',
          darkMode: data.dark_mode_active === 1 || data.dark_mode_active === true,
          flag1: data.service_area_messages?.flag1 || '',
          flag2: data.service_area_messages?.flag2 || '',
          googleAnalyticsCode: data.google_analytic_code || '',
          googleMapsLink: data.google_map_link || '',
          externalImprintUrl: data.external_imprint_URL || '',
          hideCurrency: data.hide_currency === 1 || data.hide_currency === true,
          matrixInterfaceActive: data.show_matrix_interface === 1 || data.show_matrix_interface === true,
          cloudPrinterActive: data.show_cloud_printer === 1 || data.show_cloud_printer === true,
          kioskActive: data.show_kiosk === 1 || data.show_kiosk === true,
          ...parseRedirectSettings(data),
        }
        clearDraft()
        return { success: true, data: responseData.data }
      }

      if (responseData.errors) {
        console.warn('⚠️ Validation errors:', responseData.errors)
        return { success: false, errors: responseData.errors }
      }

      console.warn('⚠️ Save response indicates failure:', responseData.message)
      return { success: false, errors: { general: [responseData.message] } }
    } catch (error: any) {
      console.error('🔴 Error saving settings:', error.message)
      console.error('🔴 Error status:', error.response?.status)
      console.error('🔴 Error response data:', JSON.stringify(error.response?.data, null, 2))

      // Handle 422 Unprocessable Entity (validation errors)
      if (error.response?.status === 422) {
        const responseData = error.response?.data
        console.warn('⚠️ Validation error response:', JSON.stringify(responseData, null, 2))

        if (responseData?.errors) {
          return { success: false, errors: responseData.errors }
        }
        if (responseData?.message) {
          return { success: false, errors: { general: [responseData.message] } }
        }
        if (responseData?.error === true) {
          return { success: false, errors: { general: [responseData.message || 'Validation error'] } }
        }
      }

      // Handle other error responses
      if (error.response?.data?.errors) {
        return { success: false, errors: error.response.data.errors }
      }

      if (error.response?.data?.message) {
        return { success: false, errors: { general: [error.response.data.message] } }
      }

      console.error('🔴 Full error details:', JSON.stringify(error.response?.data, null, 2) || error.message)
      return { success: false, errors: { general: [error.message || 'Failed to save settings'] } }
    }
  }

  return {
    settings,
    draft,
    initialize,
    clearDraft,
    initializeDraft,
    updateDraft,
    getDraft,
    setModuleVisibility,
    loadSettings,
    saveSettings,
  }
}
